import React from "react";
import {renderToStaticMarkup} from "react-dom/server";
import {connect} from "react-redux";
import styled from "styled-components";
import {coordinateApi} from "../../api/coordinate-api";
import {clearSelectedScheduleAction, setSelectedScheduleAction,} from "../../app/reducers/schedule-slice";
import {AppDispatch, RootState} from "../../app/store";
import {UPDATE_COORDINATES_LOCATION_INTERVAL} from "../../types/constant";
import {WorkerCoordinate} from "../../types/coordinates/worker-coordinate";
import {Location} from "../../types/location/location";
import {DeliveryGroup} from "../../types/schedule/delivery-group";
import {Schedule} from "../../types/schedule/schedule";
import {ScheduleStatus} from "../../types/schedule/schedule-status";
import {User} from "../../types/user/user";
import {timeFormat} from "../../utils/date/date-format";
import {createLatLng} from "../../utils/naver-map/latlng/create-latlng";
import {clearMarkers} from "../../utils/naver-map/marker/clear-markers";
import {clearPolylines} from "../../utils/naver-map/polyline/clear-polylines";
import {createGrayPolyline} from "../../utils/naver-map/polyline/create-gray-polyline";
import {createYellowPolyline} from "../../utils/naver-map/polyline/create-yellow-polyline";
import {BlackCoordinatePin} from "../pin/coordinate-pin/BlackCoordinatePin";
import {YellowCoordinatePin} from "../pin/coordinate-pin/YellowCoordinatePin";
import {GrayOutlineRoutePin} from "../pin/outline-route-pin/GrayOutlineRoutePin";
import {WhiteOutlineRoutePin} from "../pin/outline-route-pin/WhiteOutlineRoutePin";
import {YellowOutlineRoutePin} from "../pin/outline-route-pin/YellowOutlineRoutePin";
import {RoutePinContainer} from "../pin/route-pin-container/RoutePinContainer";
import {mapToCoordinate} from "../../utils/converter/coordinate/worker-coordinate-converter";

interface Props {
    selectedStatus: string[];
    selectedClientIds: string[];

    profile?: User;
    selectedSchedule?: Schedule;
    schedules: Schedule[];

    setSelectedSchedule: (schedule: Schedule) => void;
    clearSelectedSchedule: () => void;
}

interface State {
    map?: naver.maps.Map;
    workerCoordinates: WorkerCoordinate[];
    routeMarkers: naver.maps.Marker[];
    workerMarkers: naver.maps.Marker[];
    polylines: naver.maps.Polyline[];
}

class DashboardMap extends React.Component<Props, State> {
    private _interval?: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);
        this.state = {
            map: undefined,
            workerCoordinates: [],
            routeMarkers: [],
            workerMarkers: [],
            polylines: [],
        };
    }

    componentDidMount(): void {
        const newMap = new naver.maps.Map("dashboard-map", {
            center: createLatLng(37.530791, 126.9795728),
            zoom: 12,
        });
        this.setState({map: newMap}, () => {
            this.getCoordinates();
            this._interval = setInterval(() => {
                this.getCoordinates();
            }, UPDATE_COORDINATES_LOCATION_INTERVAL);
        });
    }

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>
    ): void {
        const {selectedStatus, selectedClientIds, selectedSchedule, schedules} =
            this.props;
        const {map, workerCoordinates} = this.state;

        if (map) {
            if (
                prevState.workerCoordinates !== workerCoordinates ||
                prevProps.selectedStatus !== selectedStatus ||
                prevProps.selectedClientIds !== selectedClientIds ||
                prevProps.selectedSchedule !== selectedSchedule ||
                (selectedSchedule && prevProps.schedules !== schedules) ||
                (selectedSchedule && prevProps.schedules !== schedules)
            ) {
                this.renderMapContents();
            }
        }
    }

    componentWillUnmount(): void {
        clearInterval(this._interval);
    }

    render() {
        return (
            <DashboardMapContainer>
                <DashboardMapContent id="dashboard-map"/>
            </DashboardMapContainer>
        );
    }

    getCoordinates = async () => {
        try {
            const response = await coordinateApi.getAll();

            this.setState({
                workerCoordinates: mapToCoordinate(response.data),
            });
        } catch (error) {
            console.log(error);
        }
    };

    renderRouteMarkerIcon = (deliveryGroup: DeliveryGroup, index: number) => {
        if (deliveryGroup.isWait) {
            return (
                <WhiteOutlineRoutePin
                    number={index}
                    rightLabel={deliveryGroup.formattedAddress}
                    leftLabel={deliveryGroup.displayType}
                />
            );
        } else if (deliveryGroup.isWork) {
            return (
                <YellowOutlineRoutePin
                    number={index}
                    rightLabel={deliveryGroup.formattedAddress}
                    leftLabel={deliveryGroup.displayType}
                />
            );
        }
        return (
            <GrayOutlineRoutePin
                number={index}
                rightLabel={deliveryGroup.formattedAddress}
                leftLabel={deliveryGroup.displayType}
            />
        );
    };

    renderRouteMarker = (markerIcons: JSX.Element[], index: number) => {
        const length = markerIcons.length;
        return (
            <RoutePinContainer length={length} index={index}>
                {markerIcons.map((markerIcon) => markerIcon)}
            </RoutePinContainer>
        );
    };

    showSelectedScheduleRoute = (schedule: Schedule) => {
        const {map, polylines, routeMarkers} = this.state;

        clearPolylines(polylines);
        clearMarkers(routeMarkers);

        if (!map) {
            return;
        }

        const grayPath: naver.maps.LatLng[] = [];
        const yellowPath: naver.maps.LatLng[] = [];

        let previousLocation: Location | undefined = undefined;

        const locationMap: { [key: string]: number[] } = {};

        schedule.deliveryGroups.forEach((deliveryGroup, index) => {
            if (previousLocation) {
                if (deliveryGroup.isWork) {
                    yellowPath.push(
                        createLatLng(previousLocation.latitude, previousLocation.longitude)
                    );
                    yellowPath.push(
                        createLatLng(
                            deliveryGroup.location.latitude,
                            deliveryGroup.location.longitude
                        )
                    );
                } else {
                    grayPath.push(
                        createLatLng(previousLocation.latitude, previousLocation.longitude)
                    );
                    grayPath.push(
                        createLatLng(
                            deliveryGroup.location.latitude,
                            deliveryGroup.location.longitude
                        )
                    );
                }
            }

            previousLocation = deliveryGroup.location;

            locationMap[deliveryGroup.location.id] =
                locationMap[deliveryGroup.location.id] || [];
            locationMap[deliveryGroup.location.id].push(index);
        });

        const newRouteMarkers = Object.values(locationMap).map(
            (deliveryGroupIndexes, index) => {
                const deliveryGroup = schedule.deliveryGroups[deliveryGroupIndexes[0]];

                const newRouteMarkerIconLists = deliveryGroupIndexes.map(
                    (deliveryGroupIndex) => {
                        const deliveryGroup = schedule.deliveryGroups[deliveryGroupIndex];
                        return this.renderRouteMarkerIcon(
                            deliveryGroup,
                            deliveryGroupIndex + 1
                        );
                    }
                );

                return new naver.maps.Marker({
                    position: createLatLng(
                        deliveryGroup.location.latitude,
                        deliveryGroup.location.longitude
                    ),
                    map: map,
                    icon: {
                        content: renderToStaticMarkup(
                            this.renderRouteMarker(newRouteMarkerIconLists, index)
                        ),
                    },
                });
            }
        );

        this.setState({
            routeMarkers: newRouteMarkers,
            polylines: [
                createGrayPolyline(map, grayPath),
                createYellowPolyline(map, yellowPath),
            ],
        });
    };

    showSelectedWorkerMarker = (selectedSchedule: Schedule) => {
        const {map, workerMarkers, workerCoordinates} = this.state;
        if (!map) {
            return;
        }
        clearMarkers(workerMarkers);

        const {currentDeliveryGroup} = selectedSchedule;

        const coordinate = workerCoordinates.find(
            (coordinate) => coordinate.scheduleId == selectedSchedule.id
        );

        if (!coordinate) {
            return;
        }

        const marker = new naver.maps.Marker({
            position: createLatLng(coordinate.latitude, coordinate.longitude),
            map: map,
            icon: {
                content: renderToStaticMarkup(
                    <YellowCoordinatePin
                        memberName={selectedSchedule?.alias || coordinate.workerName || ""}
                        title={selectedSchedule?.alias || ""}
                        leftBody={currentDeliveryGroup.displayType}
                        leftBodyColor={
                            currentDeliveryGroup.isPickUp ? "#0d8ce2" : "#E7AD18"
                        }
                        rightBody={currentDeliveryGroup.formattedAddress}
                        footer={timeFormat(currentDeliveryGroup.startTimeLimited)}
                    />
                ),
            },
        });

        marker.addListener("click", () => {
            this.props.clearSelectedSchedule();
        });

        this.setState({
            workerMarkers: [marker],
        });
    };

    filterSchedule(schedules: Schedule[]) {
        let filteredSchedules = schedules;
        const {selectedStatus, selectedClientIds} = this.props;
        if (selectedStatus.length > 0) {
            filteredSchedules = schedules.filter((displaySchedule) =>
                selectedStatus.includes(displaySchedule.status)
            );
        }
        if (selectedClientIds.length > 0) {
            filteredSchedules = schedules.filter((displaySchedule) =>
                selectedClientIds.some((clientId) =>
                    displaySchedule.clientIds.includes(clientId)
                )
            );
        }
        return filteredSchedules;
    }

    showWorkerMarkers = (workerCoordinates: WorkerCoordinate[]) => {
        const {schedules} = this.props;
        const filterdSchedules = this.filterSchedule(schedules);
        const {map, workerMarkers} = this.state;
        clearMarkers(workerMarkers);

        if (!map) {
            return;
        }

        const newWorkerMarkers: naver.maps.Marker[] = [];
        workerCoordinates.forEach((coordinate) => {
            const workerSchedule = filterdSchedules.find(
                (schedule) => schedule.id === coordinate.scheduleId
            );
            if (!workerSchedule) {
                return;
            }

            const {currentDeliveryGroup} = workerSchedule;

            const marker = new naver.maps.Marker({
                position: createLatLng(coordinate.latitude, coordinate.longitude),
                map: map,
                icon: {
                    content: renderToStaticMarkup(
                        <BlackCoordinatePin
                            memberName={workerSchedule?.alias || coordinate.workerName || ""}
                            title={workerSchedule?.alias || ""}
                            leftBody={currentDeliveryGroup.displayType}
                            leftBodyColor={
                                currentDeliveryGroup.isPickUp ? "#0d8ce2" : "#E7AD18"
                            }
                            rightBody={currentDeliveryGroup.formattedAddress}
                            footer={timeFormat(currentDeliveryGroup.startTimeLimited)}
                        />
                    ),
                },
            });

            marker.addListener("click", () => {
                this.handleClickCoordinatePin(coordinate.scheduleId);
            });

            newWorkerMarkers.push(marker);
        });

        this.setState({
            workerMarkers: newWorkerMarkers,
        });
    };

    handleClickCoordinatePin = (scheduleId?: string) => {
        const {schedules} = this.props;
        const selectedSchedule = schedules.find(
            (schedule) => schedule.id === scheduleId
        );
        if (selectedSchedule) {
            this.props.setSelectedSchedule(selectedSchedule);
        }
    };

    renderMapContents = () => {
        const {selectedSchedule} = this.props;
        const {workerCoordinates, workerMarkers, routeMarkers, polylines} =
            this.state;

        clearMarkers(workerMarkers);
        clearMarkers(routeMarkers);
        clearPolylines(polylines);

        if (selectedSchedule) {
            if (
                this.isSatisfyRenderMapContentsConditionWhenScheduleSelected(
                    selectedSchedule
                )
            ) {
                this.showSelectedWorkerMarker(selectedSchedule);
                this.showSelectedScheduleRoute(selectedSchedule);
            }
        } else {
            if (this.isSatisfyRenderMapContentsConditionWhenScheduleNotSelected()) {
                this.showWorkerMarkers(workerCoordinates);
            }
        }
    };

    isSatisfyRenderMapContentsConditionWhenScheduleSelected = (
        selectedSchedule: Schedule
    ) => {
        const {selectedStatus, selectedClientIds} = this.props;
        const clientIds = selectedSchedule.deliveryGroups.flatMap((group) =>
            group.deliveriesInGroup.map((delivery) => delivery.client.id)
        );
        const isFilteredSelectedClientId = selectedClientIds.some((id) =>
            clientIds.includes(id)
        );
        return (
            selectedStatus.includes(selectedSchedule.status) ||
            selectedStatus.length === 0 ||
            isFilteredSelectedClientId ||
            selectedClientIds.length === 0
        );
    };

    isSatisfyRenderMapContentsConditionWhenScheduleNotSelected = () => {
        const {selectedStatus, selectedClientIds} = this.props;
        return (
            selectedStatus.includes(ScheduleStatus.Work) ||
            selectedStatus.length === 0
        );
    };
}

const MapStateToProps = (state: RootState) => ({
    profile: state.profile.profile,
    selectedSchedule: state.schedule.selectedSchedule,
    schedules: state.schedule.schedulePageAll.contents,
});

const MapDispatchToProps = (dispatch: AppDispatch) => ({
    setSelectedSchedule: (schedule: Schedule) =>
        dispatch(setSelectedScheduleAction({selectedSchedule: schedule})),
    clearSelectedSchedule: () => dispatch(clearSelectedScheduleAction()),
});

export default connect(MapStateToProps, MapDispatchToProps)(DashboardMap);

const DashboardMapContainer = styled.div`
  height: 100%;
  width: 100%;
  position: fixed;
`;

const DashboardMapContent = styled.div`
  height: 100%;
  width: 100%;
`;
